import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarCheck, Check } from "lucide-react";
import Seo from "../seo";
import { api } from "../api";
import { Reveal, Breadcrumbs } from "../components/motion";

export default function ConditionDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null); setNotFound(false);
        api.get(`/content/conditions/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return (
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
            <h1 className="font-display text-3xl text-navy">Condition not found</h1>
            <Link to="/conditions" data-testid="back-to-conditions-btn" className="mt-6 inline-block text-accent font-semibold">Back to all conditions</Link>
        </div>
    );
    if (!data) return <div className="mx-auto max-w-7xl px-5 py-24 text-steel">Loading…</div>;

    const c = data.condition;
    return (
        <div className="mx-auto max-w-5xl px-5 py-14 lg:px-10 lg:py-20" data-testid="condition-detail">
            <Seo title={`${c.name} | Dr. Krishnakanth AVB, Urologist Madurai`} description={c.summary}
                jsonLd={{
                    "@context": "https://schema.org", "@type": "MedicalWebPage",
                    "name": c.name, "description": c.summary,
                    "about": { "@type": "MedicalCondition", "name": c.name },
                    "author": { "@type": "Physician", "name": "Dr. Krishnakanth AVB" },
                }} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Conditions", to: "/conditions" }, { label: c.name }]} />
            <Reveal>
                <p className="mt-10 font-mono-label text-accent">{c.category}</p>
                <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">{c.name}</h1>
                <p className="mt-6 max-w-3xl text-base leading-relaxed text-steel md:text-lg">{c.summary}</p>
            </Reveal>

            <div className="mt-14 grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
                <div className="space-y-12">
                    <Reveal>
                        <h2 className="font-display text-2xl font-medium text-navy">Overview</h2>
                        <p className="mt-4 text-base leading-relaxed text-steel">{c.overview}</p>
                    </Reveal>
                    <Reveal>
                        <h2 className="font-display text-2xl font-medium text-navy">Common symptoms</h2>
                        <ul className="mt-5 space-y-3">
                            {c.symptoms.map((s) => (
                                <li key={s} className="flex items-start gap-3 text-base text-steel">
                                    <Check className="mt-1 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal>
                        <div className="rounded-2xl border border-accent/20 bg-accent/5 p-7">
                            <h2 className="font-display text-xl font-medium text-navy">When to see a urologist</h2>
                            <p className="mt-3 text-base leading-relaxed text-steel">{c.when_to_see}</p>
                        </div>
                    </Reveal>
                </div>
                <aside>
                    <Reveal delay={0.1}>
                        <div className="sticky top-24 rounded-2xl border border-border bg-mist p-7">
                            <h3 className="font-display text-lg font-medium text-navy">Discuss your symptoms</h3>
                            <p className="mt-3 text-sm leading-relaxed text-steel">
                                A consultation allows proper evaluation and a treatment plan suited to your situation.
                            </p>
                            <Link to="/appointment" data-testid="condition-book-appointment-btn"
                                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent">
                                <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                                Book an Appointment
                            </Link>
                            <Link to="/treatments" data-testid="condition-explore-treatments-link"
                                className="mt-3 block text-center text-sm font-semibold text-accent hover:underline">
                                Explore related treatments
                            </Link>
                        </div>
                    </Reveal>
                </aside>
            </div>
            <p className="mt-16 border-t border-border pt-6 text-xs leading-relaxed text-slate-400">{data.disclaimer}</p>
        </div>
    );
}
